import React, { useEffect, useState } from 'react';
import { Breakpoint, Button, ControlLink, SlidingPane, Stack } from '@moda/om';
import MailIcon from '@moda/icons/mail-16';
import { SECONDS_TO_MILLISECONDS } from '@moda/portal-stanchions';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { LineItem } from '../../hooks/useCart/cart.rest';
import { BackInStockNotificationModal } from '../SizeSelector/BackInStockNotificationModal';
import { tc } from '../../lib/trackingContext';
import { SizeInventoryFragment } from '../../generated/types';
import { useCountry } from '../CountrySelector';
import { BackInStockNotificationForm } from '../SizeSelector/BackInStockNotificationForm';
import { PRODUCT_DETAIL_PAGE_QUERY } from '../../pages/ProductDetailPage/ProductDetailPageContainer';
import { useLineItemMutations, Mode } from './useLineItemMutations';
import { LineItemAvailabilityNotes } from './LineItemAvailabilityNotes';
import { LineItemImage } from './LineItemImage';
import { LineItemOverlay } from './LineItemOverlay';
import { LineItemPrice } from './LineItemPrice';
import { LineItemProductDetails } from './LineItemProductDetails';
import { LineItemFullCell as Cell } from './LineItemFullCell';
import { LineItemSizeSelector } from './LineItemSizeSelector';
import { LineItemCustomization } from './LineItemCustomization';
import { LineItemBackInStock } from './LineItemBackInStock';
import { LineItemPromo } from './LineItemPromo';
import { SavedLineItemSoldOut } from './SavedLineItemSoldOut';
import { LineItemAlerts } from './LineItemAlerts';
import { LineItemQuantitySelector } from './LineItemQuantitySelector';

import './LineItemGrid.scss';

type Props = {
  index: number;
  theme?: 'box' | 'simple';
  item: LineItem;
  showPriceWithVAT?: boolean;
  className?: string;
};

const HEADER_ROW_COUNT = 3;
const ITEM_ROW_COUNT = 4;
const COLUMN_COUNT = 5;

export const LineItemGrid: React.FC<Props> = ({
  index,
  theme = 'box',
  item,
  showPriceWithVAT,
  className
}) => {
  const { handleDelete, handleChangeSize, handleChangeType, mode, handleChangeQuantity } =
    useLineItemMutations(item);
  const headerRowCount = theme === 'box' ? HEADER_ROW_COUNT : 1;
  const startRow = headerRowCount + index * ITEM_ROW_COUNT + 1;
  const [showModal, setShowModal] = useState(false);

  const {
    country: { alpha2Code: countryCode }
  } = useCountry();

  const selectedSize = item.attributes.inventory.find(size => size.selected === true);
  const selectedSize2 = {
    __typename: 'Inventory',
    sku: selectedSize?.id.toString(),
    size: selectedSize?.size
  };

  const variantId = item.attributes.variantId.toString();

  const { data, client } = useQuery(PRODUCT_DETAIL_PAGE_QUERY, {
    variables: { variantId, countryCode }
  });

  const handleClick = () => {
    setShowModal(true);
    tc.track('SFL Notify Me Clicked', {
      addToTrackingEvent: {
        variantId: variantId,
        place: 'SavedForLater'
      }
    });
  };

  useEffect(() => {
    if (data?.variant) {
      [data.variant.masterVariant, ...data.variant.otherVariants].forEach(variant => {
        if (variant && variant.id !== variantId) {
          client.query({
            query: PRODUCT_DETAIL_PAGE_QUERY,
            variables: { variantId: variant.id, countryCode }
          });
        }
      });
    }
  }, [client, countryCode, data, variantId]);

  const ended =
    item.attributes?.activeEndAt &&
    item.attributes?.activeEndAt * SECONDS_TO_MILLISECONDS < Date.now();

  return (
    <Stack className="LineItemGrid" space={3} direction="vertical">
      <Cell
        row={startRow + 1}
        column={1}
        className={`LineItemGrid__image line-item-${item.attributes.variantId}`}
      >
        <LineItemImage item={item} className="LineItemImage__grid" />
        <div className="LineItemGrid__availability-notes">
          <LineItemAvailabilityNotes
            item={item}
            onChangeType={handleChangeType}
            className="LineItemAvailabilityNotes__grid-notes"
            largeIcon
          />
        </div>
      </Cell>

      <Cell row={startRow + 1} column={2}>
        <Stack space={3}>
          <LineItemProductDetails item={item} />
          <LineItemCustomization item={item} />
          <SavedLineItemSoldOut red item={item} />
          <LineItemBackInStock item={item} />
          <LineItemPromo item={item} />
        </Stack>
      </Cell>
      <Cell row={startRow + 1} column={5} className="LineItemGrid__price">
        <LineItemPrice
          showPriceWithVAT={showPriceWithVAT}
          direction="vertical"
          alignItems="center"
          item={item}
        />
      </Cell>
      <Cell row={startRow + 1} column={3} columnSpan={3} className="LineItemGrid__alerts">
        <LineItemAlerts item={item} />
      </Cell>
      <Cell
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        row={startRow + 3}
        column={1}
        columnSpan={COLUMN_COUNT}
        className="LineItemGrid__actions"
      >
        <Breakpoint gt="sm">
          <Stack
            justifyContent="center"
            className="LineItemGrid__selectors"
            space={3}
            direction="horizontal"
            alignItems="center"
          >
            <LineItemQuantitySelector item={item} onChange={handleChangeQuantity} useFlex />
            <LineItemSizeSelector item={item} onChange={handleChangeSize} useFlex />
          </Stack>
        </Breakpoint>
        <Breakpoint lt="sm">
          <Stack
            justifyContent="center"
            className="LineItemGrid__selectors LineItemGrid__selectors-top"
            space={3}
            direction="vertical"
            alignItems="center"
          >
            <LineItemQuantitySelector item={item} onChange={handleChangeQuantity} useFlex />
            <LineItemSizeSelector item={item} onChange={handleChangeSize} useFlex />
          </Stack>
        </Breakpoint>
        <Stack
          className="LineItemGrid__selectors"
          space={3}
          direction="vertical"
          alignItems="center"
        >
          {item.attributes.isSoldOut ? (
            <Button
              onClick={handleClick}
              data-testid="savedForLatermoveToBag-notify"
              className="LineItemGrid__notify-button LineItemGrid__button-wide"
            >
              <MailIcon />
              &nbsp;Notify Me
            </Button>
          ) : item.attributes.isTrunkshow && ended ? (
            <Button
              className="LineItemGrid__bag-button"
              data-testid={`savedForLatermoveToBag-${index}`}
              disabled
            >
              trunkshow has ended
            </Button>
          ) : (
            <Button
              className={classNames('LineItemGrid__bag-button', className)}
              onClick={() => handleChangeType('CartItem')}
              data-testid={`savedForLatermoveToBag-${index}`}
            >
              Move to bag
            </Button>
          )}
          <ControlLink
            className="LineItemGrid__remove"
            data-testid={`savedForLaterRemove-${index}`}
            onClick={handleDelete}
          >
            Remove
          </ControlLink>
        </Stack>
      </Cell>

      {mode !== Mode.Resting && (
        <Cell
          row={startRow}
          rowSpan={ITEM_ROW_COUNT}
          column={1}
          columnSpan={COLUMN_COUNT}
          className="LineItemGrid__overlay"
        >
          <LineItemOverlay item={item} mode={mode} show />
        </Cell>
      )}
      {data?.variant && (
        <>
          <Breakpoint gt="md">
            <BackInStockNotificationModal
              show={showModal}
              activeVariantId={item.attributes.variantId.toString()}
              variant={data.variant}
              size={selectedSize2 as SizeInventoryFragment}
              onClose={() => setShowModal(false)}
              place={'savedForLater'}
            />
          </Breakpoint>
          <Breakpoint lt="md">
            <SlidingPane title="Notify Me" visible={showModal} onClose={() => setShowModal(false)}>
              <BackInStockNotificationForm
                activeVariantId={item.attributes.variantId.toString()}
                variant={data.variant}
                size={selectedSize2 as SizeInventoryFragment}
                onClose={() => setShowModal(false)}
              />
            </SlidingPane>
          </Breakpoint>
        </>
      )}
    </Stack>
  );
};
